import { useEffect, useState } from "react";
import { BrowserRouter } from "react-router-dom";
import { IonApp } from "@ionic/react";
import { Capacitor } from "@capacitor/core";
import { Providers } from "./app/providers";
import { IonicContextProvider } from "./app/IonicContextProvider";
import { AppRouter } from "./app/router";
import { navigationService } from "./lib/navigation";
import {
  checkForceUpgrade,
  ForceUpgradeResult,
  getPreviewForceUpgradeResult,
} from "./lib/versionCheck";
import { ForceUpgradeScreen } from "./components/ForceUpgradeScreen";
import "./lib/deferredDeepLinkTester"; // Import for development testing

const HOST = "sawadone-4d94d35e4cba.herokuapp.com";

function stashUid(uid: string) {
  localStorage.setItem("pendingInviteUid", uid);
  // SPA navigation – no reload
  const url = `/initialize?uid=${encodeURIComponent(uid)}`;
  
  // Check if we're in a browser environment and location is available
  if (typeof window !== 'undefined' && window.location) {
    const currentPath = window.location.pathname;
    const currentSearch = window.location.search;
    const expectedSearch = `?uid=${encodeURIComponent(uid)}`;
    
    if (currentPath !== "/initialize" || currentSearch !== expectedSearch) {
      window.history.replaceState({}, "", url);
    }
  }
}

export default function AppComponent() {
  const previewForceUpgrade = getPreviewForceUpgradeResult();
  const [forceUpgrade, setForceUpgrade] = useState<ForceUpgradeResult | null>(null);
  const [forceUpgradeCheckDone, setForceUpgradeCheckDone] = useState(false);

  useEffect(() => {
    const initializeApp = async () => {
      if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === "android") {
        const result = await checkForceUpgrade();
        setForceUpgrade(result.upgradeRequired ? result : null);
      }
      setForceUpgradeCheckDone(true);
      await navigationService.initialize();
    };

    initializeApp();

    return () => {
      navigationService.cleanup();
    };
  }, []);

  // Register service worker when running in browser so PWA install prompt can show
  useEffect(() => {
    if (!Capacitor.isNativePlatform() && "serviceWorker" in navigator) {
      navigator.serviceWorker
        .register("/firebase-messaging-sw.js")
        .then((reg) => {
          if (reg.installing) reg.installing.addEventListener("statechange", () => {});
        })
        .catch(() => {});
    }
  }, []);

  // Note: Deep link and launch URL handling is now managed by NavigationService
  // to avoid duplicate listeners and memory leaks

  if (
    !forceUpgradeCheckDone &&
    Capacitor.isNativePlatform() &&
    Capacitor.getPlatform() === "android" &&
    !previewForceUpgrade
  ) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  const forceUpgradeToShow = forceUpgrade ?? previewForceUpgrade;
  if (forceUpgradeToShow) {
    return <ForceUpgradeScreen result={forceUpgradeToShow} />;
  }

  return (
    <IonApp className="pb-safe h-full">
      <BrowserRouter>
        <IonicContextProvider>
          <Providers>
            <AppRouter />
          </Providers>
        </IonicContextProvider>
      </BrowserRouter>
    </IonApp>
  );
}

